import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Table, Card, Nav, NavItem, NavLink, TabContent, TabPane,
} from 'reactstrap';
import { Field } from 'redux-form';
import classnames from 'classnames';
import Panel from '../../../../../shared/components/Panel';
import renderSelectField from '../../../../../shared/components/form/Select';
import {
  typeOptions,
  statusOptions,
  editRoleOptions,
  viewRoleOptions,
} from './utils';
import {
  getTableHeaderStyle,
  getTableRowStyle,
  getTableWrapperStyle,
  isDarkTheme,
} from '../../../../../shared/helpers/themeTableStyles';

export default function GroupSettingsPanel({
  initialLoad, getData, dynamicValueField, onReset, onDelete, onToggle, groups, activeTab, apiData, copyAccountSettings, changedFields, isUpdating, onBulkUpdate, onFieldChange,
}) {
  const changedCount = Object.keys(changedFields).length;
  const theme = useSelector(store => store.theme);
  const isDarkMode = isDarkTheme(theme);

  return (
    <Panel lg={12} title="Settings By Group" getData={getData} parentRefresh={initialLoad}>
      <Card>
        <div className="profile__card tabs tabs--bordered-bottom">
          <div className="tabs__wrap">
            <Nav tabs>
              {groups && groups.map(group => (
                <NavItem key={group.value}>
                  <NavLink
                    className={classnames({ active: activeTab === group.label })}
                    onClick={() => {
                      onToggle(group.label);
                    }}
                  >
                    {group.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
            <TabContent activeTab={activeTab}>
              <TabPane tabId="Default" key="default">
                <p>Select tab above to start.</p>
              </TabPane>
              {groups && groups.map(group => (
                <TabPane tabId={group.label} key={group.value}>
                  <div style={getTableWrapperStyle(isDarkMode)}>
                    <Table hover responsive className="table table--bordered">
                      <thead style={{ backgroundColor: isDarkMode ? '#232531' : '#f8f9fa' }}>
                        <tr>
                          <th style={getTableHeaderStyle(isDarkMode)}>#</th>
                          <th style={getTableHeaderStyle(isDarkMode)}>Key</th>
                          <th style={getTableHeaderStyle(isDarkMode)}>Value</th>
                          <th style={getTableHeaderStyle(isDarkMode)}>Type</th>
                          <th style={getTableHeaderStyle(isDarkMode)}>Status</th>
                          <th style={getTableHeaderStyle(isDarkMode)}>Edit Role</th>
                          <th style={getTableHeaderStyle(isDarkMode)}>View Role</th>
                          <th style={getTableHeaderStyle(isDarkMode)}>Group</th>
                          <th style={{ ...getTableHeaderStyle(isDarkMode), width: '100px' }}>Update</th>
                          <th style={{ ...getTableHeaderStyle(isDarkMode), width: '100px' }}>Create</th>
                          <th style={{ ...getTableHeaderStyle(isDarkMode), width: '160px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apiData && apiData.filter(line => (((line.setting_group || '').toLowerCase() === group.value) || (group.value === ''))).map((line, index) => {
                          const rowStyle = getTableRowStyle(isDarkMode, index % 2 === 1);
                          return (
                            <tr key={line.account_setting_id} style={rowStyle}>
                              <td style={rowStyle}>{line.account_setting_id}</td>
                              <td style={rowStyle}>
                                <span className="form">
                                  <Field
                                    name={`${line.account_setting_id}Key`}
                                    component="input"
                                    className="form"
                                    onBlur={() => onFieldChange(line.account_setting_id, 'Key')}
                                  />
                                </span>
                              </td>
                              <td style={rowStyle}>
                                <span className="form">
                                  {dynamicValueField(`${line.account_setting_id}Value`, line.account_setting_id, () => onFieldChange(line.account_setting_id, 'Value'))}
                                </span>
                              </td>
                              <td style={rowStyle}>
                                <Field
                                  name={`${line.account_setting_id}Type`}
                                  component={renderSelectField}
                                  type="text"
                                  options={typeOptions}
                                  onChange={(event, newValue) => onFieldChange(line.account_setting_id, 'Type', newValue)}
                                />
                              </td>
                              <td style={rowStyle}>
                                <Field
                                  name={`${line.account_setting_id}Status`}
                                  component={renderSelectField}
                                  type="text"
                                  options={statusOptions}
                                  onChange={(event, newValue) => onFieldChange(line.account_setting_id, 'Status', newValue)}
                                />
                              </td>
                              <td style={rowStyle}>
                                <Field
                                  name={`${line.account_setting_id}EditRole`}
                                  component={renderSelectField}
                                  type="text"
                                  options={editRoleOptions}
                                  onChange={(event, newValue) => onFieldChange(line.account_setting_id, 'EditRole', newValue)}
                                />
                              </td>
                              <td style={rowStyle}>
                                <Field
                                  name={`${line.account_setting_id}ViewRole`}
                                  component={renderSelectField}
                                  type="text"
                                  options={viewRoleOptions}
                                  onChange={(event, newValue) => onFieldChange(line.account_setting_id, 'ViewRole', newValue)}
                                />
                              </td>
                              <td style={rowStyle}>
                                <Field
                                  name={`${line.account_setting_id}Group`}
                                  component={renderSelectField}
                                  type="text"
                                  options={groups}
                                  onChange={(event, newValue) => onFieldChange(line.account_setting_id, 'Group', newValue)}
                                />
                              </td>
                              <td style={{ ...rowStyle, whiteSpace: 'nowrap' }}>{line.last_updated_by} - {line.last_updated_ts}</td>
                              <td style={{ ...rowStyle, whiteSpace: 'nowrap' }}>{line.created_by} - {line.created_ts}</td>
                              <td style={rowStyle}>
                                <div style={{ display: 'grid', gridTemplateColumns: '70px 70px', gap: '12px' }}>
                                  <div>
                                    {changedFields[line.account_setting_id] && (
                                      <button type="button" className="badge badge-warning badge-btn" onClick={() => onReset(line.account_setting_id)}>Reset</button>
                                    )}
                                  </div>
                                  <div>
                                    <button type="button" className="badge badge-danger badge-btn" onClick={() => onDelete(line.account_setting_id, line.setting_key)}>Delete</button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </TabPane>
              ))}
            </TabContent>
            <p>Quick Actions:</p>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => onBulkUpdate()}
                disabled={isUpdating || changedCount === 0}
              >
                {isUpdating ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: '5px' }} />
                    Updating...
                  </>
                ) : (
                  `Update All Changes ${changedCount > 0 ? `(${changedCount})` : ''}`
                )}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => copyAccountSettings()}>
                Copy Default System Account Settings
              </button>
            </div>
          </div>
        </div>
      </Card>
    </Panel>
  );
}

GroupSettingsPanel.propTypes = {
  initialLoad: PropTypes.bool.isRequired,
  getData: PropTypes.func.isRequired,
  dynamicValueField: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  groups: PropTypes.instanceOf(Array).isRequired,
  activeTab: PropTypes.string.isRequired,
  apiData: PropTypes.instanceOf(Array).isRequired,
  copyAccountSettings: PropTypes.func.isRequired,
  changedFields: PropTypes.instanceOf(Object).isRequired,
  isUpdating: PropTypes.bool.isRequired,
  onBulkUpdate: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};
